import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import ConfirmModal from '../components/ConfirmModal';

const ProfileDetailPage = () => {
  const { profileId } = useParams();
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const { success, error } = useToast();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await api.get(`/matrimony/profiles/${profileId}`);
        setProfile(data);
      } catch (err) {
        console.error('Error loading profile:', err);
        error('Profile not found or could not be loaded.');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [profileId, error]);

  const handleDeleteProfile = async () => {
    setDeleting(true);
    try {
      await api.delete('/matrimony/profile');
      success('Profile deleted successfully');
      navigate('/matrimony');
    } catch (err) {
      error('Failed to delete profile');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-border" style={{ width: '3rem', height: '3rem', color: 'var(--primary-500)' }}></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="error-container">
        <h2>Profile Not Found</h2>
        <Link to="/matrimony" className="btn btn-primary">
          Back to Profiles
        </Link>
      </div>
    );
  }

  const isOwner = currentUser && currentUser.uid === profile.userId;
  const imageUrl = profile.profilePhotoUrl || 'https://placehold.co/600x600/E3F2FD/2E5C8A?text=Profile';

  return (
    <div className="profile-detail-page">
      <div className="container">
        <div className="profile-layout">
          {/* Sidebar */}
          <aside className="profile-sidebar">
            <div className="sidebar-card">
              <div className="profile-image-large">
                <img src={imageUrl} alt={profile.fullName} />
                <div className="verified-badge">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
              </div>

              <div className="profile-quick-info">
                <h2>{profile.fullName}</h2>
                <div className="quick-stats">
                  <div className="stat-item">
                    <span className="stat-label">Age</span>
                    <span className="stat-value">{profile.age} yrs</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Height</span>
                    <span className="stat-value">{profile.height || '—'}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Religion</span>
                    <span className="stat-value">{profile.religion || 'Hindu'}</span>
                  </div>
                </div>
              </div>

              {isOwner ? (
                <div className="owner-actions">
                  <button onClick={() => navigate('/create-profile')} className="btn btn-primary w-full">
                    Edit Profile
                  </button>
                  <button onClick={() => setShowDeleteModal(true)} className="btn btn-outline w-full">
                    Delete Profile
                  </button>
                </div>
              ) : (
                <div className="visitor-actions">
                  <button className="btn btn-primary w-full">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                    Express Interest
                  </button>
                  <button className="btn btn-outline w-full">
                    Send Message
                  </button>
                </div>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <main className="profile-main">
            {/* About Section */}
            <section className="info-section">
              <h3 className="section-title">About</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Full Name</span>
                  <span className="info-value">{profile.fullName}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Gender</span>
                  <span className="info-value">{profile.gender}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Date of Birth</span>
                  <span className="info-value">{profile.dob || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Marital Status</span>
                  <span className="info-value">{profile.maritalStatus || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Blood Group</span>
                  <span className="info-value">{profile.bloodGroup || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Mother Tongue</span>
                  <span className="info-value">{profile.motherTongue || '—'}</span>
                </div>
              </div>
            </section>

            {/* Education & Career */}
            <section className="info-section">
              <h3 className="section-title">Education & Career</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Education</span>
                  <span className="info-value">{profile.education || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Occupation</span>
                  <span className="info-value">{profile.occupation || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Company</span>
                  <span className="info-value">{profile.company || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Work Location</span>
                  <span className="info-value">{profile.workLocation || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Annual Income</span>
                  <span className="info-value">{profile.annualIncome || '—'}</span>
                </div>
              </div>
            </section>

            {/* Lifestyle */}
            <section className="info-section">
              <h3 className="section-title">Lifestyle</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Diet</span>
                  <span className="info-value">{profile.diet || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Smoking</span>
                  <span className="info-value">{profile.smoking || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Drinking</span>
                  <span className="info-value">{profile.drinking || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Hobbies</span>
                  <span className="info-value">{profile.hobbies || '—'}</span>
                </div>
              </div>
            </section>

            {/* Family Details */}
            <section className="info-section">
              <h3 className="section-title">Family Details</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Father's Name</span>
                  <span className="info-value">{profile.fatherName || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Father's Occupation</span>
                  <span className="info-value">{profile.fatherOccupation || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Mother's Name</span>
                  <span className="info-value">{profile.motherName || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Mother's Occupation</span>
                  <span className="info-value">{profile.motherOccupation || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Brothers</span>
                  <span className="info-value">{profile.brothers || 0}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Sisters</span>
                  <span className="info-value">{profile.sisters || 0}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Family Status</span>
                  <span className="info-value">{profile.familyStatus || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Native Place</span>
                  <span className="info-value">{profile.nativePlace || '—'}</span>
                </div>
              </div>
            </section>

            {/* Partner Preferences */}
            <section className="info-section">
              <h3 className="section-title">Partner Preferences</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Age Range</span>
                  <span className="info-value">{profile.partnerAgeRange || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Height</span>
                  <span className="info-value">{profile.partnerHeight || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Education</span>
                  <span className="info-value">{profile.partnerEducation || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Occupation</span>
                  <span className="info-value">{profile.partnerOccupation || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Location</span>
                  <span className="info-value">{profile.partnerLocation || '—'}</span>
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section className="info-section">
              <h3 className="section-title">Contact Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Phone</span>
                  <span className="info-value">{profile.contactNumber || 'Hidden'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{profile.email || 'Hidden'}</span>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteProfile}
        title="Delete Profile"
        message="Are you sure you want to delete your profile? This action cannot be undone."
        confirmText="Delete"
        type="danger"
        loading={deleting}
      />

      <style jsx>{`
        .profile-detail-page {
          min-height: 100vh;
          padding: var(--space-12) 0;
          background: var(--neutral-50);
        }

        .profile-layout {
          display: grid;
          grid-template-columns: 380px 1fr;
          gap: var(--space-8);
        }

        .profile-sidebar {
          position: sticky;
          top: var(--space-20);
          height: fit-content;
        }

        .sidebar-card {
          background: white;
          border-radius: var(--radius-xl);
          padding: var(--space-6);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--neutral-200);
        }

        .profile-image-large {
          position: relative;
          width: 100%;
          aspect-ratio: 1;
          border-radius: var(--radius-lg);
          overflow: hidden;
          margin-bottom: var(--space-6);
        }

        .profile-image-large img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .verified-badge {
          position: absolute;
          top: var(--space-4);
          right: var(--space-4);
          width: 36px;
          height: 36px;
          background: var(--success);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .profile-quick-info h2 {
          font-size: var(--text-2xl);
          margin-bottom: var(--space-6);
          text-align: center;
        }

        .quick-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--space-4);
          margin-bottom: var(--space-6);
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: var(--space-3);
          background: var(--neutral-50);
          border-radius: var(--radius-md);
        }

        .stat-label {
          font-size: var(--text-xs);
          color: var(--neutral-600);
          margin-bottom: var(--space-1);
        }

        .stat-value {
          font-size: var(--text-base);
          font-weight: var(--weight-semibold);
          color: var(--neutral-900);
        }

        .owner-actions,
        .visitor-actions {
          display: flex;
          flex-direction: column;
          gap: var(--space-3);
        }

        .w-full {
          width: 100%;
        }

        .profile-main {
          display: flex;
          flex-direction: column;
          gap: var(--space-6);
        }

        .info-section {
          background: white;
          border-radius: var(--radius-xl);
          padding: var(--space-6);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--neutral-200);
        }

        .section-title {
          font-size: var(--text-xl);
          margin-bottom: var(--space-6);
          padding-bottom: var(--space-4);
          border-bottom: 2px solid var(--neutral-200);
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--space-6);
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: var(--space-1);
        }

        .info-label {
          font-size: var(--text-sm);
          font-weight: var(--weight-medium);
          color: var(--neutral-600);
        }

        .info-value {
          font-size: var(--text-base);
          color: var(--neutral-900);
        }

        .loading-container,
        .error-container {
          min-height: 60vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: var(--space-4);
        }

        @media (max-width: 768px) {
          .profile-layout {
            grid-template-columns: 1fr;
          }

          .profile-sidebar {
            position: static;
          }

          .info-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileDetailPage;